// Route: /courses/catalog/$templateId — LEGACY адрес карточки курса автора.
//
// ─────────────────────────────────────────────────────────────────────────────
// Почему это не «просто редирект»
// ─────────────────────────────────────────────────────────────────────────────
// Публичная витрина курса живёт на `/catalog/{slug}`, и опубликованный курс
// обязан уводить туда: два адреса одной страницы — это два canonical, дубли в
// индексе и расхождение SEO-кэша. Поэтому сначала спрашиваем `resolveSlug`.
//
// Но на ЭТОТ ЖЕ адрес ведёт «Мои шаблоны» (`template-mine-card.tsx`), включая
// ЧЕРНОВИКИ и снятые с публикации шаблоны. У них публичной страницы нет и быть
// не может (`slug: null`), и увести автора на «курс не найден» с собственного
// черновика значило бы сломать раздел, который сегодня работает. Поэтому при
// `slug: null` роут остаётся тем, чем был, — приватной карточкой шаблона.
//
// Итог: три исхода, и ни один не выдумывает адрес.
//   slug есть      → 301-подобный `replace` на `/catalog/{slug}`;
//   slug === null  → приватная карточка (черновик автора / снят с публикации);
//   шаблона нет    → «курс не найден» со ссылкой на каталог.
//
// Не вложен в `/courses/catalog` (имя файла с `_`): родитель — редирект без
// `<Outlet/>`, и вложенный child молча не отрисовался бы (`rules/code-quality.md` §35).

import { Button } from "@studyhub/ui/components/button";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { IconArrowLeft } from "@tabler/icons-react";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useResolveTemplateSlug } from "@/components/courses/catalog/_api";
import { useCourseTemplate } from "@/components/courses/templates/_api";
import { TemplateDetailView } from "@/components/courses/templates/template-detail-view";
import { LIST_PAGE_SHELL } from "@/components/shared/entity-list";
import { OfflineDetailState } from "@/components/shared/offline-detail-state";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useOnlineStatus } from "@/hooks/use-online-status";

export const Route = createFileRoute("/courses/catalog_/$templateId")({
	component: CourseTemplateLegacyPage,
	pendingComponent: PagePendingFallback,
});

function DetailSkeleton(): React.ReactElement {
	return (
		<div className="space-y-3">
			{[0, 1, 2].map((index) => (
				<div
					key={index}
					aria-busy="true"
					className="h-24 animate-pulse rounded-[var(--radius-card)] border border-border bg-card shadow-e1"
				/>
			))}
		</div>
	);
}

function CourseTemplateLegacyPage(): React.ReactElement {
	const { templateId } = Route.useParams();
	const resolved = useResolveTemplateSlug(templateId);

	// Пока slug неизвестен — скелетон, а не приватная карточка: иначе на секунду
	// показывался бы один экран, а потом рывком другой.
	if (resolved.isLoading) {
		return (
			<div className={LIST_PAGE_SHELL}>
				<DetailSkeleton />
			</div>
		);
	}

	const slug = resolved.data?.slug ?? null;
	if (slug !== null) {
		return <Navigate to="/catalog/$slug" params={{ slug }} replace />;
	}

	return <PrivateTemplateCard templateId={templateId} />;
}

/**
 * Приватная карточка шаблона — то, чем этот адрес был до появления витрины.
 *
 * Живёт для случаев, у которых публичной страницы нет: собственный черновик и
 * снятый с публикации шаблон в разделе «Мои шаблоны».
 */
function PrivateTemplateCard({ templateId }: { templateId: string }): React.ReactElement {
	const { t } = useTranslation("courses");
	const online = useOnlineStatus();
	const query = useCourseTemplate(templateId);
	const template = query.data ?? null;

	// «Нет такого шаблона» и «не смогли загрузить» — разные сообщения: первое
	// окончательно, второе лечится повтором.
	const notFound = query.error?.data?.code === "NOT_FOUND";

	return (
		<div className={LIST_PAGE_SHELL}>
			<div className="flex items-center gap-2">
				<Button asChild variant="ghost" size="sm" className="-ml-2 cursor-pointer">
					<Link to="/catalog" preload="viewport">
						<IconArrowLeft size={14} />
						{t("templates.detail.back")}
					</Link>
				</Button>
			</div>

			<PrivateTemplateBody
				online={online}
				isLoading={query.isLoading}
				isError={query.isError}
				notFound={notFound}
				template={template}
				onRetry={() => void query.refetch()}
			/>
		</div>
	);
}

interface PrivateTemplateBodyProps {
	online: boolean;
	isLoading: boolean;
	isError: boolean;
	notFound: boolean;
	template: React.ComponentProps<typeof TemplateDetailView>["template"] | null;
	onRetry: () => void;
}

function PrivateTemplateBody({
	online,
	isLoading,
	isError,
	notFound,
	template,
	onRetry,
}: PrivateTemplateBodyProps): React.ReactElement {
	const { t } = useTranslation("courses");

	if (isLoading) return <DetailSkeleton />;

	// Офлайн раньше ошибки: карточка шаблона не хранится на устройстве.
	if (!online && template === null) {
		return (
			<OfflineDetailState
				title={t("catalog.offlineTitle")}
				description={t("catalog.offlineHint")}
				onRetry={onRetry}
			/>
		);
	}

	if (isError || template === null) {
		return (
			<ErrorState
				title={notFound ? t("templates.detail.notFoundTitle") : t("catalog.errorTitle")}
				description={notFound ? t("templates.detail.notFoundHint") : t("catalog.errorHint")}
				action={
					notFound ? (
						<Button asChild variant="outline" className="cursor-pointer">
							<Link to="/catalog" preload="intent">
								{t("catalog.public.backToCatalog")}
							</Link>
						</Button>
					) : (
						<Button variant="outline" className="cursor-pointer" onClick={onRetry}>
							{t("catalog.retry")}
						</Button>
					)
				}
			/>
		);
	}

	return <TemplateDetailView template={template} />;
}
